import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiEye, FiHeart, FiAward } = FiIcons;

const About = () => {
  const team = [
    {
      name: 'Jessica Miller',
      role: 'Lead Interior Designer',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      description: 'With 15 years of experience, Jessica leads our design team with creativity and precision.'
    },
    {
      name: 'David Rodriguez',
      role: 'Senior Designer',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      description: 'David specializes in modern and minimalist designs, bringing clean aesthetics to every project.'
    },
    {
      name: 'Amanda Chen',
      role: 'Project Manager',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      description: 'Amanda ensures every project runs smoothly from concept to completion.'
    }
  ];

  const values = [
    {
      icon: FiTarget,
      title: 'Our Mission',
      description: 'To create exceptional interior spaces that enhance the way people live and work, combining functionality with aesthetic beauty.'
    },
    {
      icon: FiEye,
      title: 'Our Vision',
      description: 'To be the leading interior design firm known for innovative solutions and outstanding client satisfaction.'
    },
    {
      icon: FiHeart,
      title: 'Our Values',
      description: 'Creativity, integrity, excellence, and client-focused approach guide everything we do.'
    },
    {
      icon: FiAward,
      title: 'Our Promise',
      description: 'We promise to deliver designs that exceed expectations and create spaces you\'ll love for years to come.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Elite Interior Design | Professional Design Team</title>
        <meta name="description" content="Learn about Elite Interior Design team, our mission, vision, and values. Meet our experienced designers who create exceptional interior spaces." />
        <meta name="keywords" content="about interior design, design team, interior designers, company mission, professional designers" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-5xl font-bold text-white mb-6">About Elite Interior Design</h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                We are passionate interior designers dedicated to creating beautiful, functional spaces 
                that reflect your unique style and enhance your quality of life.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-white mb-6">Our Story</h2>
                <p className="text-gray-300 mb-6">
                  Founded in 2014, Elite Interior Design began as a small studio with a big vision: 
                  to transform spaces into extraordinary environments that inspire and delight. What started 
                  as a passion project has grown into a full-service design firm serving clients across the region.
                </p>
                <p className="text-gray-300 mb-6">
                  Our journey has been marked by countless successful projects, satisfied clients, and industry 
                  recognition. We've had the privilege of designing everything from cozy residential homes to 
                  large commercial spaces, each project teaching us something new and pushing us to innovate.
                </p>
                <p className="text-gray-300">
                  Today, we continue to be driven by the same passion that started it all: creating spaces 
                  that not only look beautiful but also enhance the way people live and work.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                  alt="Our Studio"
                  className="rounded-lg shadow-lg"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-white mb-4">Our Core Values</h2>
              <p className="text-gray-300 max-w-2xl mx-auto">
                These principles guide our work and define who we are as a design team.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <SafeIcon icon={value.icon} className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-300">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-white mb-4">Meet Our Team</h2>
              <p className="text-gray-300 max-w-2xl mx-auto">
                Our talented team of designers and project managers work together to bring your vision to life.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="text-center"
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-48 h-48 rounded-full mx-auto mb-4 object-cover"
                  />
                  <h3 className="text-xl font-bold text-white mb-2">{member.name}</h3>
                  <p className="text-yellow-400 mb-3">{member.role}</p>
                  <p className="text-gray-300">{member.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Awards Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h2 className="text-4xl font-bold text-white mb-8">Awards & Recognition</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-gray-800 p-6 rounded-lg">
                  <SafeIcon icon={FiAward} className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Best Interior Design 2023</h3>
                  <p className="text-gray-300">Regional Design Excellence Award</p>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg">
                  <SafeIcon icon={FiAward} className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Client Choice Award 2022</h3>
                  <p className="text-gray-300">Outstanding Customer Satisfaction</p>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg">
                  <SafeIcon icon={FiAward} className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Innovation in Design 2021</h3>
                  <p className="text-gray-300">Creative Excellence Recognition</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;